import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { offsetsApi, type OffsetDto, type CreateOffsetRequest } from '../../api/offsets';
import { accountsApi } from '../../api/accounts';
import { personsApi } from '../../api/persons';
import { formatMoney } from '../../utils/formatMoney';
import { formatDate, monthsAgo, today } from '../../utils/formatDate';
import DataTable from '../../components/DataTable';
import DatePickerInput from '../../components/DatePickerInput';
import LoadingSpinner from '../../components/LoadingSpinner';
import MoneyCell from '../../components/MoneyCell';
import { useToast } from '../../components/Toast';
import { useReportScreen } from '../../components/ScreenContext';
import { useDocumentSerialPreview } from '../../hooks/useDocumentSerial';
import { useIsMirror } from '../../hooks/useIsMirror';

export default function OffsetList() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [from, setFrom] = useState(monthsAgo(3));
  const [to, setTo] = useState(today());
  const [showForm, setShowForm] = useState(false);
  const [selectedOffset, setSelectedOffset] = useState<OffsetDto | null>(null);
  // ADR-004: mirror workspaces (mysql, asmx-owned) are READ-ONLY — hide the
  // write affordances; the API 403s them anyway (defense in depth).
  const isMirror = useIsMirror();

  const { data: offsets, isLoading } = useQuery({
    queryKey: ['offsets', from, to],
    queryFn: () => offsetsApi.list({ from, to }),
  });

  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list({ limit: 500 }),
  });

  const { data: persons } = useQuery({
    queryKey: ['persons-offsets'],
    queryFn: () => personsApi.list({ limit: 500 }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => offsetsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offsets'] });
      toast.addToast('Offset deleted', 'success');
    },
    onError: (err: Error) => toast.addToast(`Error: ${err.message}`, 'error'),
  });

  const accountMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const a of accounts ?? []) map.set(a.accountNumber, a.name);
    return map;
  }, [accounts]);

  const personMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of persons ?? []) map.set(p.id, p.name);
    return map;
  }, [persons]);

  const totalCount = offsets?.length ?? 0;
  const totalAmount = (offsets ?? []).reduce((sum, o) => sum + o.amount, 0);

  useReportScreen(`Offsets (${totalCount})`, {
    recordCount: totalCount,
    filters: useMemo(() => ({ from, to }), [from, to]),
  });

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Payment Offsets</h1>
        {!isMirror && (
          <button
            className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
            onClick={() => { setShowForm(true); setSelectedOffset(null); }}
          >
            + New Offset
          </button>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <div className="text-xs text-gray-500">Total Count</div>
          <div className="text-lg font-semibold num">{totalCount}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <div className="text-xs text-gray-500">Total Amount</div>
          <div className="text-lg font-semibold num"><MoneyCell value={totalAmount} /></div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <DatePickerInput label="From" value={from} onChange={setFrom} inputClassName="w-36" />
        <DatePickerInput label="To" value={to} onChange={setTo} inputClassName="w-36" />
      </div>

      {/* Create/Edit form */}
      {showForm && !isMirror && (
        <OffsetForm
          offset={selectedOffset}
          accounts={accounts ?? []}
          persons={persons ?? []}
          onSubmit={async (data) => {
            if (selectedOffset) {
              await offsetsApi.update(selectedOffset.id, data);
              toast.addToast('Offset updated', 'success');
            } else {
              await offsetsApi.create(data);
              toast.addToast('Offset created', 'success');
            }
            queryClient.invalidateQueries({ queryKey: ['offsets'] });
            setShowForm(false);
            setSelectedOffset(null);
          }}
          onCancel={() => { setShowForm(false); setSelectedOffset(null); }}
        />
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <DataTable
          columns={[
            {
              key: 'date',
              header: 'Date',
              render: (row) => formatDate(row.date),
              className: 'whitespace-nowrap',
            },
            { key: 'documentNumber', header: 'Doc #', className: 'whitespace-nowrap' },
            {
              key: 'accountDebit',
              header: 'Debit Acct',
              render: (row) => {
                const name = accountMap.get(row.accountDebit);
                return name ? `${row.accountDebit} ${name}` : String(row.accountDebit || '-');
              },
              className: 'max-w-40 truncate',
            },
            {
              key: 'accountCredit',
              header: 'Credit Acct',
              render: (row) => {
                const name = accountMap.get(row.accountCredit);
                return name ? `${row.accountCredit} ${name}` : String(row.accountCredit || '-');
              },
              className: 'max-w-40 truncate',
            },
            {
              key: 'amount',
              header: 'Amount',
              render: (row) => <span className="num">{formatMoney(row.amount)}</span>,
              className: 'text-right',
            },
            { key: 'currencyCode', header: 'Currency', className: 'text-center' },
            {
              key: 'personId',
              header: 'Person',
              render: (row) => row.personId ? (personMap.get(row.personId) ?? `#${row.personId}`) : '-',
              className: 'max-w-32 truncate',
            },
            {
              key: 'actions',
              header: '',
              render: (row) => (
                <div className="flex gap-2 justify-end">
                  {!isMirror && (
                    <>
                      <button className="text-xs text-primary-600 hover:underline"
                        onClick={(e) => { e.stopPropagation(); setSelectedOffset(row); setShowForm(true); }}>
                        Edit
                      </button>
                      <button className="text-xs text-red-500 hover:underline"
                        onClick={(e) => { e.stopPropagation(); if (confirm(`Delete offset #${row.id}?`)) deleteMutation.mutate(row.id); }}>
                        Delete
                      </button>
                    </>
                  )}
                </div>
              ),
              className: 'text-right',
            },
          ]}
          data={offsets ?? []}
          keyFn={(row) => row.id}
          emptyMessage="No offsets found for this period"
        />
      </div>
    </div>
  );
}

function OffsetForm({ offset, accounts, persons, onSubmit, onCancel }: {
  offset: OffsetDto | null;
  accounts: { accountNumber: number; name: string }[];
  persons: { id: number; name: string }[];
  onSubmit: (data: CreateOffsetRequest) => Promise<void>;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<CreateOffsetRequest>({
    date: offset?.date ?? today(),
    documentNumber: offset?.documentNumber ?? '',
    content: offset?.content ?? '',
    accountDebit: offset?.accountDebit ?? 0,
    accountCredit: offset?.accountCredit ?? 0,
    amount: offset?.amount ?? 0,
    personId: offset?.personId ?? null,
    currencyCode: offset?.currencyCode ?? 'EUR',
    currencyRate: 1,
  });
  const [saving, setSaving] = useState(false);

  // Auto-numbering from document serial
  const { previewNumber, autoSerialCode, autoFilled, setAutoFilled } = useDocumentSerialPreview('Offset', form.date);

  // Auto-fill document number from serial when no explicit number set
  useEffect(() => {
    if (!offset && previewNumber && !form.documentNumber && !autoFilled) {
      setForm(f => ({ ...f, documentNumber: previewNumber }));
      setAutoFilled(true);
    }
  }, [previewNumber, offset, form.documentNumber, autoFilled, setAutoFilled]);

  const handleSubmit = async () => {
    setSaving(true);
    const explicitNumber = form.documentNumber.trim();
    try {
      await onSubmit({
        ...form,
        documentNumber: explicitNumber || '',
        autoSerialCode: !explicitNumber && autoSerialCode ? autoSerialCode : undefined,
      });
    } catch (err) { alert(err instanceof Error ? err.message : 'Error'); }
    setSaving(false);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h2 className="font-semibold text-gray-900 mb-3">
        {offset ? 'Edit Offset' : 'New Offset'}
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Date *</label>
          <input type="date" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.date?.substring(0, 10) ?? ''} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Document Number *</label>
          <input type="text"
            className={`w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md ${!form.documentNumber && previewNumber ? 'placeholder:text-gray-400' : ''}`}
            value={form.documentNumber} onChange={(e) => setForm({ ...form, documentNumber: e.target.value })}
            placeholder={previewNumber || 'e.g. SK-001'} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Amount *</label>
          <input type="number" step="0.01" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.amount || ''} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Person</label>
          <select className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.personId ?? ''} onChange={(e) => setForm({ ...form, personId: e.target.value ? parseInt(e.target.value) : null })}>
            <option value="">-- None --</option>
            {persons.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Debit Account</label>
          <select className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.accountDebit} onChange={(e) => setForm({ ...form, accountDebit: parseInt(e.target.value) || 0 })}>
            <option value={0}>-- Select --</option>
            {accounts.map(a => <option key={a.accountNumber} value={a.accountNumber}>{a.accountNumber} {a.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Credit Account</label>
          <select className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.accountCredit} onChange={(e) => setForm({ ...form, accountCredit: parseInt(e.target.value) || 0 })}>
            <option value={0}>-- Select --</option>
            {accounts.map(a => <option key={a.accountNumber} value={a.accountNumber}>{a.accountNumber} {a.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Currency</label>
          <select className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.currencyCode ?? 'EUR'} onChange={(e) => setForm({ ...form, currencyCode: e.target.value })}>
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
            <option value="GBP">GBP</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Content</label>
          <input type="text" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
        </div>
        <div className="flex items-end gap-2 col-span-2">
          <button
            className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            onClick={handleSubmit}
            disabled={saving || (!form.documentNumber && !autoSerialCode) || form.amount <= 0}
          >
            {saving ? 'Saving...' : (offset ? 'Update' : 'Create')}
          </button>
          <button className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
